import React, { useEffect, useState } from 'react';
import { X, Loader2, ExternalLink } from 'lucide-react';
import { getDocumentUrl } from '../lib/api';
import { useT } from '../lib/LanguageContext';

/**
 * Inline image lightbox modal — same fetch-signed-URL pattern as PdfViewer,
 * rendering an <img> instead of an <iframe>.
 */
export default function ImageViewer({ docId, title, onClose, fetchUrl }) {
  const t = useT();
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!docId) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await (fetchUrl ? fetchUrl() : getDocumentUrl(docId));
        if (!cancelled) setUrl(d.url);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || t('viewer.imageFailed'));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex flex-col"
      data-testid="image-viewer-backdrop"
      onClick={onClose}
    >
      <div
        className="flex items-center gap-2 px-3 h-12 bg-keep-surface border-b border-keep-border"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-keep-hover"
          aria-label={t('common.close')}
          data-testid="image-viewer-close"
        >
          <X className="w-5 h-5 text-keep-textMuted" />
        </button>
        <span className="text-[14px] font-medium text-keep-text truncate flex-1">
          {title || t('source.image')}
        </span>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="p-1.5 rounded-full hover:bg-keep-hover"
            title={t('viewer.openInNewTab')}
            data-testid="image-viewer-newtab"
          >
            <ExternalLink className="w-4 h-4 text-keep-textMuted" />
          </a>
        )}
      </div>
      <div
        className="flex-1 bg-black/90 overflow-auto flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {error ? (
          <div className="h-full flex items-center justify-center p-6">
            <p className="text-red-400 text-[14px]" data-testid="image-viewer-error">{error}</p>
          </div>
        ) : !url ? (
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        ) : (
          <img
            src={url}
            alt={title || t('source.image')}
            className="max-w-full max-h-full object-contain"
            data-testid="image-viewer-img"
          />
        )}
      </div>
    </div>
  );
}
